import { useHandler } from "@redotech/react-util/hook";
import {
  memo,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import { RedoButton } from "../buttons/redo-button";

export interface FilterOverflowContainerProps {
  children: ReactNode;
  onViewAllClick: () => void;
  maxRows?: number;
  onOverflowChange?: (isOverflowing: boolean) => void;
  onResetFiltersClick?: () => void;
  showResetFilters?: boolean;
}

export const FilterOverflowContainer = memo(function FilterOverflowContainer({
  children,
  onViewAllClick,
  maxRows = 2,
  onOverflowChange,
  onResetFiltersClick,
  showResetFilters = false,
}: FilterOverflowContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [totalFilterCount, setTotalFilterCount] = useState(0);
  const lastCalculatedStateRef = useRef({
    isOverflowing: false,
    childCount: 0,
  });
  const [chipHeight, setChipHeight] = useState<number | null>(null);
  const [gapSize, setGapSize] = useState<number | null>(null);

  const calculateOverflow = useHandler(() => {
    const container = containerRef.current;
    if (!container || chipHeight === null || gapSize === null) return;

    const childCount = container.children.length;

    if (childCount === 0) {
      setIsOverflowing(false);
      setTotalFilterCount(0);
      lastCalculatedStateRef.current = { isOverflowing: false, childCount: 0 };
      return;
    }

    // Let the browser determine if content overflows by comparing scrollHeight to clientHeight
    // Add a small tolerance (1px) to account for sub-pixel rendering
    const isOverflowing = container.scrollHeight > container.clientHeight + 1;

    // Only update if state actually changed
    if (
      lastCalculatedStateRef.current.isOverflowing !== isOverflowing ||
      lastCalculatedStateRef.current.childCount !== childCount
    ) {
      lastCalculatedStateRef.current = { isOverflowing, childCount };
      setTotalFilterCount(childCount);
      setIsOverflowing(isOverflowing);
      if (onOverflowChange) {
        onOverflowChange(isOverflowing);
      }
    }
  });

  // Measure chip dimensions and calculate overflow
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || container.children.length === 0) {
      setChipHeight(null);
      setGapSize(null);
      return;
    }

    const firstChild = container.children[0] as HTMLElement;
    const computedStyle = getComputedStyle(container);

    const measuredChipHeight = firstChild.offsetHeight;
    const measuredGapSize = parseInt(computedStyle.gap) || 8;

    setChipHeight(measuredChipHeight);
    setGapSize(measuredGapSize);
  }, [children, maxRows]);

  // Recalculate overflow when dimensions or children change
  useLayoutEffect(() => {
    if (chipHeight !== null && gapSize !== null) {
      calculateOverflow();
    }
  }, [children, chipHeight, gapSize, calculateOverflow]);

  // Watch for container size changes (e.g., window resize, parent container resize)
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(calculateOverflow);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [calculateOverflow]);

  const handleViewAll = useHandler(() => {
    onViewAllClick();
  });

  const handleResetFilters = useHandler(() => {
    if (onResetFiltersClick) {
      onResetFiltersClick();
    }
  });

  // Calculate max height based on measured chip dimensions
  // Only set maxHeight once we have measured dimensions to avoid layout shift
  const maxHeightValue =
    chipHeight !== null && gapSize !== null
      ? maxRows * (chipHeight + gapSize) - gapSize
      : undefined;

  return (
    <Flex dir="column" gap="md">
      <Flex
        align="center"
        dir="row"
        gap="sm"
        ref={containerRef}
        style={{
          overflow: "hidden",
          // Set maxHeight based on measured dimensions to prevent ResizeObserver feedback loop
          maxHeight:
            maxHeightValue !== undefined ? `${maxHeightValue}px` : undefined,
        }}
        wrap="wrap"
      >
        {children}
      </Flex>
      {(isOverflowing || showResetFilters) && (
        <Flex dir="row" gap="md">
          {isOverflowing && (
            <RedoButton
              hierarchy="tertiary"
              onClick={handleViewAll}
              size="sm"
              text={`View all filters (${totalFilterCount})`}
            />
          )}
          {showResetFilters && (
            <RedoButton
              hierarchy="tertiary"
              onClick={handleResetFilters}
              size="sm"
              text="Reset filters"
            />
          )}
        </Flex>
      )}
    </Flex>
  );
});
